import { Injectable } from "@angular/core";
import { UtilService } from "@core/data/util.service";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { FeatureFlag } from "@core/interfaces/entities/feature-flag";
import { isMaintenanceResponse, redirectToMaintenancePage, setMaintenanceModeActive } from "@core/utils/maintenance-mode.util";

@Injectable({
    providedIn: "root",
})
export class FeatureFlagsService {
    private flags: BehaviorSubject<FeatureFlag | null> = new BehaviorSubject<FeatureFlag | null>(null);

    constructor(private readonly httpClient: UtilService) {}

    populate(shouldRefresh: boolean = false): Observable<FeatureFlag> {
        if (!shouldRefresh && this.flags.getValue()) {
            return this.flags.asObservable();
        }
        return this.httpClient.getFeatureFlags().pipe(
            catchError((error) => {
                if (isMaintenanceResponse(error)) {
                    setMaintenanceModeActive();
                    redirectToMaintenancePage();
                }

                return of({});
            }),
            switchMap((flags: FeatureFlag) => {
                this.flags.next(flags);
                return this.flags.asObservable();
            }),
        );
    }

    getFeatureFlags(): Observable<FeatureFlag> {
        return this.flags.asObservable();
    }

    getFeatureFlagsSync(): FeatureFlag {
        return this.flags.getValue();
    }

    getFeatureFlag(flag: string): boolean {
        return !!this.getFeatureFlagsSync()?.[flag];
    }

    isFeatureEnabled(flag: string): boolean {
        return !!this.getFeatureFlag(flag);
    }

    isFeatureDisabled(flag: string): boolean {
        return !this.isFeatureEnabled(flag);
    }
}
